import { useState, createContext, useCallback, useContext, memo, useMemo, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import Cookie from 'universal-cookie'
import Router from 'next/router'
import {
  getUserDetails,
  getUserDetailsKey,
  getDealerDetails,
  getDealerDetailsKey,
} from '../api-client/identity'
import {
  getBasicDetails,
  getBasicDetailsKey,
} from '../api-client/account'
import { tokenKey } from '../api-client/client'
import { UserRole } from '../api-client/enums'
import AuthModal, { VIEWS } from '../containers/auth/modals'
import { permissions } from '../constants'
import { now } from '../utils/date-service'

const AuthContext = createContext()
const AuthDataContext = createContext()

const cookie = new Cookie()

const generateAccessTokenExpiry = () => {
  const date = now()
  date.setDate(date.getDate() + 7)

  return date
}

function AuthProviderComponent({ initialState, children }) {
  const { asPath, query, replace } = useRouter()
  const queryClient = useQueryClient()
  const [authModal, setAuthModal] = useState({ open: false, view: VIEWS.Login })
  const [accessToken, setAccessToken] = useState(() => {
    if (initialState.accessToken) return initialState.accessToken

    const token = cookie.get(tokenKey)

    return token || null
  })

  const isAuthenticated = !!accessToken

  const clearLoginData = (queryClient) => {
    setAccessToken(null)
    cookie.remove(tokenKey, { path: '/' })
    queryClient.clear()

    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.clear()
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.clear()
    }
  }

  const loadLoginData = (accessToken) => {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.clear()
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.clear()
    }

    setAccessToken(() => {
      cookie.set(tokenKey, accessToken, { path: '/', expires: generateAccessTokenExpiry() })
      return accessToken
    })
  }

  const authResync = useCallback(() => {
    let lastAccessToken = accessToken

    console.log('attempting user auth info resync')

    clearLoginData(queryClient)
    loadLoginData(lastAccessToken)
  }, [accessToken, queryClient])

  const login = useCallback(
    ({ accessToken }) => {
      loadLoginData(accessToken)

      if (query?.authRedirect) {
        Router.push(query.authRedirect)
      }
    },
    [query?.authRedirect],
  )

  const logout = useCallback(() => {
    clearLoginData(queryClient)

    Router.push('/')
  }, [queryClient])

  useEffect(() => {
    if (query?.authModal) setAuthModal({ open: true, view: VIEWS.Login })
  }, [query?.authModal])

  const [isAuthFinishedFetching, setIsAuthFinishedFetching] = useState(false)

  const { data: userDetails, isFetching: isUserDetailsFetching } = useQuery({
    queryKey: getUserDetailsKey,
    queryFn: getUserDetails,
    enabled: isAuthenticated,
  })

  const { data: basicDetails, isFetching: isBasicDetailsFetching } = useQuery({
    queryKey: getBasicDetailsKey,
    queryFn: getBasicDetails,
    enabled: isAuthenticated,
  })

  const getIsDealer = (userDetails) => {
    return (
      (userDetails?.roles ?? []).filter(
        (ur) => ur === UserRole.Dealer || ur === UserRole.Wholesaler || ur === UserRole.Fleet,
      ).length > 0
    )
  }

  const { data: dealerDetails, isFetching: isDealerDetailsFetching } = useQuery({
    queryKey: getDealerDetailsKey,
    queryFn: getDealerDetails,
    enabled: isAuthenticated && !!getIsDealer(userDetails),
  })

  useEffect(() => {
    if (isAuthenticated) {
      if (!!isUserDetailsFetching) {
        return
      }
      if (!!isBasicDetailsFetching) {
        return
      }
      if (!!isDealerDetailsFetching) {
        return
      }
      if (!userDetails) {
        return
      }
      if (!!getIsDealer(userDetails) && !dealerDetails) {
        return
      }
    }
    setIsAuthFinishedFetching(true)
  }, [
    isUserDetailsFetching,
    isBasicDetailsFetching,
    userDetails,
    isDealerDetailsFetching,
    dealerDetails,
    isAuthenticated,
  ])

  const handleAuthModalClose = useCallback(
    () => setAuthModal({ open: false, view: VIEWS.Login }),
    [],
  )

  /**
   * Passthrough Authentication
   */
  useEffect(() => {
    if (query.bearer) {
      if (!isAuthenticated) {
        login({ accessToken: query.bearer })
      }

      // remove token from url
      replace(asPath.split('?')[0], undefined, { shallow: true })
    }
  }, [replace, asPath, query, login, isAuthenticated])

  function getPermissions(isDealer) {
    // dealer permissions
    if (isDealer) return []

    // user permissions
    return [permissions.viewKbbData, permissions.manageCashOffer, permissions.managePreQualify]
  }

  return (
    <AuthContext.Provider
      value={useMemo(() => ({ login, logout, setAuthModal, authResync }), [
        login,
        logout,
        setAuthModal,
        authResync,
      ])}
    >
      <AuthDataContext.Provider
        value={useMemo(
          () => ({
            userDetails: {
              ...userDetails,
              ...basicDetails,
              isDealer: getIsDealer(userDetails),
              permissions: getPermissions(getIsDealer(userDetails)),
            },
            dealerDetails,
            isAuthenticated,
            isAuthFinishedFetching,
          }),
          [
            userDetails,
            basicDetails,
            dealerDetails,
            isAuthenticated,
            isAuthFinishedFetching,
          ],
        )}
      >
        {isAuthenticated && !isAuthFinishedFetching ? null : children}

        {authModal.open && (
          <AuthModal onClose={handleAuthModalClose} defaultView={authModal.view} />
        )}
      </AuthDataContext.Provider>
    </AuthContext.Provider>
  )
}

const useAuth = () => [useContext(AuthDataContext), useContext(AuthContext)]

const AuthProvider = memo(AuthProviderComponent)

export { AuthProvider, useAuth }
